import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ComprovacaoContratoResumo } from '../hooks/useComprovacaoContratoMap';

interface Props {
  resumo?: ComprovacaoContratoResumo;
}

/**
 * Badge mínimo de status de comprovação por contrato.
 * Mostra: status (cor + texto) + linha curta de contexto quando aplicável.
 * Texto explícito ("Em dia", "Atenção", "Crítico") reduz dependência da legenda
 * e melhora acessibilidade (não depende apenas de cor).
 */
export const ComprovacaoStatusBadge: React.FC<Props> = ({ resumo }) => {
  if (!resumo || resumo.total_boletins === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const { status_risco, total_em_atraso, total_boletins, criticos, pior_atraso_dias } = resumo;

  if (status_risco === 'critico') {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-col gap-0.5 cursor-help">
              <Badge variant="destructive" className="w-fit text-[10px] px-1.5 py-0">
                🔴 Crítico
              </Badge>
              <span className="text-[11px] text-muted-foreground">
                {criticos}/{total_boletins} críticos · {pior_atraso_dias}d
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Comprovação crítica — ação imediata
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (status_risco === 'atencao') {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-col gap-0.5 cursor-help">
              <Badge variant="warning" className="w-fit text-[10px] px-1.5 py-0">
                🟡 Atenção
              </Badge>
              <span className="text-[11px] text-muted-foreground">
                {total_em_atraso}/{total_boletins} em atraso
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Comprovação atrasada — atenção
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col gap-0.5 cursor-help">
            <Badge variant="success" className="w-fit text-[10px] px-1.5 py-0">
              🟢 Em dia
            </Badge>
            <span className="text-[11px] text-muted-foreground">{total_boletins} boletins</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          Comprovação em dia
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
